/**
 * Wantai European selling deck — renderer (DR-2026-261).
 *
 * A hand-rolled, dependency-free, full-screen slideshow (reveal.js-*style*, not
 * the library — see DR-2026-261 for why: the SSR heavy-lib externalization trap
 * is avoided entirely). SSR-safe: the keyboard listener attaches in a
 * client-only useEffect, and the server renders the first slide's markup.
 *
 * Navigation: ← / PageUp (back), → / Space / PageDown (forward), Home / End
 * (first / last), plus on-screen click zones and prev/next buttons. Every slide
 * is rendered into the DOM (only the active one shows on screen) so the
 * @media print stylesheet can lay the whole deck out one-slide-per-page for
 * PDF hand-off.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/wantai-logo.png';
import mark from '../../assets/wantai-mark.png';
import { getDeck, pick, gateFlag, backLabel } from './deckData';
import './Deck.scss';

const SlideContent = ({ slide, index, gateLabel }) => {
  const isCover = slide.kind === 'cover';
  const Title = isCover ? 'h1' : 'h2';

  return (
    <div className="wt-slide__inner">
      {!isCover && (
        <header className="wt-slide__topbar">
          <img className="wt-slide__logo" src={logo} alt="WANTAI BioPharm" />
          <span className="wt-slide__num">
            {String(index + 1).padStart(2, '0')}
          </span>
        </header>
      )}

      {isCover && (
        <img className="wt-slide__mark" src={mark} alt="" aria-hidden="true" />
      )}

      <div className="wt-slide__body">
        {slide.kicker && <p className="wt-slide__kicker">{slide.kicker}</p>}
        <Title className="wt-slide__title">{slide.title}</Title>
        {slide.lead && <p className="wt-slide__lead">{slide.lead}</p>}

        {slide.points && (
          <ul className="wt-slide__points">
            {slide.points.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        )}

        {slide.columns && (
          <div className="wt-slide__columns">
            {slide.columns.map((col, i) => (
              <div className="wt-slide__col" key={i}>
                <h3 className="wt-slide__col-head">{col.head}</h3>
                <p className="wt-slide__col-body">{col.body}</p>
              </div>
            ))}
          </div>
        )}

        {slide.gated && slide.gatedNote && (
          <div className="wt-slide__gate" role="note">
            <span className="wt-slide__gate-flag">{gateLabel}</span>
            <span className="wt-slide__gate-text">{slide.gatedNote}</span>
          </div>
        )}
      </div>

      {slide.footnote && (
        <footer className="wt-slide__footnote">{slide.footnote}</footer>
      )}

      {isCover && (
        <img className="wt-slide__cover-logo" src={logo} alt="WANTAI BioPharm" />
      )}
    </div>
  );
};

const Deck = () => {
  // The languageroot in the route (/en/deck | /zh-cn/deck) selects the slide set.
  const { pathname } = useLocation();
  const langRoot = pathname.split('/')[1] === 'zh-cn' ? 'zh-cn' : 'en';
  const slides = getDeck(langRoot);
  const total = slides.length;

  const [current, setCurrent] = useState(0);

  const go = useCallback(
    (n) => setCurrent(() => Math.max(0, Math.min(total - 1, n))),
    [total],
  );
  const next = useCallback(
    () => setCurrent((c) => Math.min(total - 1, c + 1)),
    [total],
  );
  const prev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);

  // Present full-screen: hide VLT header/footer and lock page scroll while the
  // deck is mounted. Reverted on unmount so leaving the route restores chrome.
  // (The Volto editor toolbar is deliberately left visible — it is the logged-in
  // presenter's way back out of the deck route.)
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('wt-deck-open');
    return () => root.classList.remove('wt-deck-open');
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          prev();
          break;
        case 'Home':
          go(0);
          break;
        case 'End':
          go(total - 1);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, go]);

  return (
    <div
      className="wt-deck"
      role="region"
      aria-roledescription="slide deck"
      aria-label="Wantai European selling deck"
    >
      {slides.map((slide, i) => (
        <section
          key={slide.id}
          className={[
            'wt-deck__slide',
            `wt-slide--${slide.kind}`,
            i === current ? 'is-active' : '',
            slide.gated ? 'is-gated' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden={i === current ? undefined : true}
        >
          <SlideContent slide={slide} index={i} gateLabel={pick(gateFlag, langRoot)} />
        </section>
      ))}

      {/* Back to the site — the deck hides the site chrome, so it needs its
          own way out. Link to /en (the growth-plan home); leaving the route
          unmounts the deck and the effect cleanup restores the chrome. */}
      <Link
        to={`/${langRoot}`}
        className="wt-deck__back"
        aria-label="Back to the site"
        title="Back to the site"
      >
        {pick(backLabel, langRoot)}
      </Link>

      {/* Click zones: left half = back, right half = forward. */}
      <button
        type="button"
        className="wt-deck__zone wt-deck__zone--prev"
        onClick={prev}
        aria-label="Previous slide"
        tabIndex={-1}
      />
      <button
        type="button"
        className="wt-deck__zone wt-deck__zone--next"
        onClick={next}
        aria-label="Next slide"
        tabIndex={-1}
      />

      <nav className="wt-deck__chrome" aria-label="Deck navigation">
        <button
          type="button"
          className="wt-deck__btn"
          onClick={prev}
          disabled={current === 0}
          aria-label="Previous slide"
        >
          ‹
        </button>
        <span className="wt-deck__counter">
          {current + 1} <span className="wt-deck__counter-sep">/</span> {total}
        </span>
        <button
          type="button"
          className="wt-deck__btn"
          onClick={next}
          disabled={current === total - 1}
          aria-label="Next slide"
        >
          ›
        </button>
      </nav>

      <div className="wt-deck__progress" aria-hidden="true">
        <div
          className="wt-deck__progress-bar"
          style={{ width: `${((current + 1) / total) * 100}%` }}
        />
      </div>
    </div>
  );
};

export default Deck;
